import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';

import { ToolsService } from '../../services/tools.service';
import { ViewerCoreService } from '../../services/viewer-core.service';
import { DropdownComponent } from '../../ui';
import { ButtonComponent } from '../../ui/button/button.component';

@Component({
  selector: 'app-clipping-panel',
  imports: [CommonModule, DropdownComponent, ButtonComponent, TranslocoPipe],
  templateUrl: './clipping-panel.component.html',
  styleUrls: ['./clipping-panel.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClippingPanelComponent {
  protected tools = inject(ToolsService);
  private core = inject(ViewerCoreService);

  volumes = signal<
    {
      id: string;
      name: string;
      clip: boolean;
      scale: [number, number, number];
      visible?: boolean;
    }[]
  >([]);
  selectedVolumeId = signal<string | null>(null);
  volumeScaleX = 1;
  volumeScaleY = 1;
  volumeScaleZ = 1;
  private readonly _volumeDetails = signal<{
    id: string;
    name: string;
    clip: boolean;
    position: [number, number, number];
    rotationDeg: [number, number, number];
    scale: [number, number, number];
    volume: number;
  } | null>(null);
  get volumeDetails() {
    return this._volumeDetails();
  }

  get currentClipTask(): string {
    return this.tools.clipTask();
  }

  get clipTaskOptions() {
    return this.tools.getClipTaskOptions();
  }

  constructor() {
    effect(onCleanup => {
      const viewer: any = this.core.viewer();
      if (!viewer?.scene) return;

      let currentScene = viewer.scene;
      const syncSelected = (): void => {
        this.refreshVolumes();
        if (!this.selectedVolumeId()) {
          this._volumeDetails.set(null);
          return;
        }

        this._volumeDetails.set(this.tools.getVolumeDetails(this.selectedVolumeId()!) ?? null);
      };

      const attachSceneListeners = (scene: any): void => {
        if (!scene) return;
        scene.addEventListener?.('update', syncSelected);
        scene.addEventListener?.('volume_added', syncSelected);
        scene.addEventListener?.('volume_removed', syncSelected);
      };

      const detachSceneListeners = (scene: any): void => {
        if (!scene) return;
        scene.removeEventListener?.('update', syncSelected);
        scene.removeEventListener?.('volume_added', syncSelected);
        scene.removeEventListener?.('volume_removed', syncSelected);
      };

      const handleSceneChanged = (event: any): void => {
        detachSceneListeners(currentScene);
        currentScene = event.scene ?? viewer.scene;
        attachSceneListeners(currentScene);
        syncSelected();
      };

      attachSceneListeners(currentScene);
      viewer.addEventListener?.('update', syncSelected);
      viewer.addEventListener?.('scene_changed', handleSceneChanged);
      syncSelected();

      onCleanup(() => {
        viewer.removeEventListener?.('update', syncSelected);
        viewer.removeEventListener?.('scene_changed', handleSceneChanged);
        detachSceneListeners(currentScene);
      });
    });
  }

  onClipTaskChange(task: string): void {
    this.tools.clipTask.set(task);
  }

  startClipVolume(): void {
    const id = this.tools.startClipVolume();
    setTimeout(() => {
      this.refreshVolumes();
      if (id) {
        this.selectVolume(id);
      } else if (this.volumes().length) {
        this.selectVolume(this.volumes()[this.volumes().length - 1].id);
      }
    }, 50);
  }

  refreshVolumes(): void {
    this.volumes.set(this.tools.listVolumes());
    if (this.selectedVolumeId()) {
      const existing = this.volumes().find(v => v.id === this.selectedVolumeId());
      if (!existing) this.selectedVolumeId.set(null);
    }
    if (this.selectedVolumeId()) {
      this._volumeDetails.set(this.tools.getVolumeDetails(this.selectedVolumeId()!));
    } else {
      this._volumeDetails.set(null);
    }
  }

  selectVolume(id: string): void {
    this.selectedVolumeId.set(id);
    this.tools.selectVolume(id);
    const v = this.volumes().find(vv => vv.id === id);
    if (v) {
      this.volumeScaleX = v.scale[0];
      this.volumeScaleY = v.scale[1];
      this.volumeScaleZ = v.scale[2];
    }
    this._volumeDetails.set(this.tools.getVolumeDetails(id));
  }

  onSelectVolume(id: string): void {
    this.selectVolume(id);
  }

  onToggleVolumeClip(vol: { id: string; clip: boolean }): void {
    this.tools.setVolumeClip(vol.id, !vol.clip);
    this.refreshVolumes();
  }

  onRemoveVolume(id: string, event?: Event): void {
    event?.stopPropagation();
    this.tools.removeVolume(id);
    if (this.selectedVolumeId() === id) this.selectedVolumeId.set(null);
    this.refreshVolumes();
  }
}
